import { ChangeEvent, useEffect, useMemo, useState } from "react";
import * as THREE from "three";
import ThreePreview from "./components/ThreePreview";
import {
  buildLithophane,
  LithophaneResult,
  LithophaneSettings,
  LithophaneShape,
} from "./lib/lithophane";
import { downloadBinaryStl } from "./lib/exportStl";
import { LoadedImage, loadImageFile } from "./lib/image";

const DEFAULT_SETTINGS: LithophaneSettings = {
  shape: "flat",
  widthMm: 120,
  heightMm: 90,
  diameterMm: 60,
  curveAngleDeg: 120,
  minThicknessMm: 0.8,
  maxThicknessMm: 3,
  samplePitchMm: 0.5,
  borderMm: 2,
  brightness: 0,
  contrast: 10,
  gamma: 1,
  invert: false,
  maxTriangles: 450_000,
};

const numberValue = (event: ChangeEvent<HTMLInputElement>) =>
  Number(event.target.value);

function formatInteger(value: number): string {
  return new Intl.NumberFormat("en-US", {
    maximumFractionDigits: 0,
  }).format(value);
}

function formatDecimal(value: number, digits = 2): string {
  return new Intl.NumberFormat("en-US", {
    minimumFractionDigits: digits,
    maximumFractionDigits: digits,
  }).format(value);
}

function safeFilename(value: string): string {
  const cleaned = value
    .trim()
    .replace(/[^a-zA-Z0-9-_]+/g, "-")
    .replace(/^-+|-+$/g, "");
  return cleaned || "lithophane";
}

export default function App() {
  const [settings, setSettings] =
    useState<LithophaneSettings>(DEFAULT_SETTINGS);
  const [loadedImage, setLoadedImage] = useState<LoadedImage | null>(null);
  const [result, setResult] = useState<LithophaneResult | null>(null);
  const [previewMode, setPreviewMode] =
    useState<"solid" | "backlit">("backlit");
  const [lockAspectRatio, setLockAspectRatio] = useState(true);
  const [outputName, setOutputName] = useState("lithophane");
  const [status, setStatus] = useState(
    "Upload a photograph to begin. Processing stays on this device.",
  );
  const [error, setError] = useState<string | null>(null);
  const [isGenerating, setIsGenerating] = useState(false);

  useEffect(
    () => () => {
      result?.geometry.dispose();
    },
    [result],
  );

  const mappedWidthMm = useMemo(
    () =>
      settings.shape === "cylinder"
        ? Math.PI * settings.diameterMm
        : settings.widthMm,
    [settings.shape, settings.diameterMm, settings.widthMm],
  );

  const updateSettings = (updates: Partial<LithophaneSettings>) => {
    setSettings((current) => {
      const next = { ...current, ...updates };
      if (lockAspectRatio && loadedImage) {
        const nextMappedWidth =
          next.shape === "cylinder"
            ? Math.PI * next.diameterMm
            : next.widthMm;
        next.heightMm =
          nextMappedWidth *
          (loadedImage.element.naturalHeight /
            loadedImage.element.naturalWidth);
      }
      return next;
    });
    setResult(null);
  };

  const handleImage = async (event: ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    setError(null);
    setStatus("Reading the photograph locally…");

    try {
      const image = await loadImageFile(file);
      setLoadedImage(image);
      setOutputName(`${safeFilename(image.filename)}-${settings.shape}`);

      const nextMappedWidth =
        settings.shape === "cylinder"
          ? Math.PI * settings.diameterMm
          : settings.widthMm;

      if (lockAspectRatio) {
        setSettings((current) => ({
          ...current,
          heightMm:
            nextMappedWidth *
            (image.element.naturalHeight / image.element.naturalWidth),
        }));
      }

      setResult(null);
      setStatus(
        `${image.element.naturalWidth} × ${image.element.naturalHeight} image loaded. Ready to generate.`,
      );
    } catch (caught) {
      setError(
        caught instanceof Error ? caught.message : "The image could not be loaded.",
      );
      setStatus("No image loaded.");
    } finally {
      event.target.value = "";
    }
  };

  const handleShape = (shape: LithophaneShape) => {
    updateSettings({ shape });
    setOutputName((current) => {
      const base = current.replace(/-(flat|curved|cylinder)$/, "");
      return `${base}-${shape}`;
    });
  };

  const generate = async () => {
    if (!loadedImage) {
      setError("Upload a photograph before generating the lithophane.");
      return;
    }

    setError(null);
    setIsGenerating(true);
    setStatus("Building the watertight 3D mesh in your browser…");

    await new Promise<void>((resolve) =>
      requestAnimationFrame(() => resolve()),
    );

    try {
      const nextResult = buildLithophane(loadedImage.element, settings);
      setResult((current) => {
        current?.geometry.dispose();
        return nextResult;
      });
      setStatus(
        `Generated ${formatInteger(nextResult.stats.triangles)} triangles at approximately ${formatDecimal(nextResult.stats.effectivePitchMm)} mm per sample.`,
      );
    } catch (caught) {
      setError(
        caught instanceof Error
          ? caught.message
          : "The lithophane could not be generated.",
      );
      setStatus("Generation failed.");
    } finally {
      setIsGenerating(false);
    }
  };

  const exportStl = () => {
    if (!result) return;

    try {
      downloadBinaryStl(
        result.geometry,
        `${safeFilename(outputName)}.stl`,
      );
      setStatus("Binary STL created and downloaded.");
      setError(null);
    } catch (caught) {
      setError(
        caught instanceof Error ? caught.message : "The STL export failed.",
      );
    }
  };

  const geometryForPreview: THREE.BufferGeometry | null =
    result?.geometry ?? null;

  return (
    <main className="app-shell">
      <header className="site-header">
        <a className="brand" href="/" aria-label="RSB23 Lithophane Studio">
          <span className="brand-mark">R</span>
          <span>
            <strong>RSB23</strong>
            <small>Lithophane Studio</small>
          </span>
        </a>
        <div className="privacy-chip">
          <span className="privacy-dot" />
          Photos stay in your browser
        </div>
      </header>

      <section className="hero">
        <div>
          <p className="eyebrow">PHOTO-TO-STL WORKSHOP</p>
          <h1>Turn a photograph into a printable lithophane.</h1>
          <p className="hero-copy">
            Build flat panels, curved displays, or complete cylindrical shades.
            Preview the geometry, simulate backlighting, and export a compact
            binary STL without uploading the photograph to a server.
          </p>
        </div>
        <div className="hero-badge">
          <strong>3 formats</strong>
          <span>Flat · Curved · Cylinder</span>
        </div>
      </section>

      <section className="workspace">
        <aside className="controls-panel">
          <div className="panel-heading">
            <div>
              <span>Configuration</span>
              <h2>Build settings</h2>
            </div>
            <span className="step-number">01</span>
          </div>

          <label className="upload-zone">
            <input type="file" accept="image/*" onChange={handleImage} />
            <span className="upload-icon">＋</span>
            <strong>
              {loadedImage ? "Replace photograph" : "Choose a photograph"}
            </strong>
            <small>JPG, PNG, WEBP, or another browser-readable image</small>
          </label>

          {loadedImage && (
            <div className="source-card">
              <img src={loadedImage.dataUrl} alt="Uploaded source" />
              <div>
                <strong>{loadedImage.filename}</strong>
                <span>
                  {loadedImage.element.naturalWidth} ×{" "}
                  {loadedImage.element.naturalHeight} pixels
                </span>
              </div>
            </div>
          )}

          <fieldset className="control-group">
            <legend>Shape</legend>
            <div className="shape-options">
              {(
                [
                  ["flat", "Flat", "Standard panel"],
                  ["curved", "Curved", "Open arc"],
                  ["cylinder", "Cylinder", "Full 360°"],
                ] as const
              ).map(([value, label, description]) => (
                <label
                  className={`shape-option ${
                    settings.shape === value ? "selected" : ""
                  }`}
                  key={value}
                >
                  <input
                    type="radio"
                    name="shape"
                    checked={settings.shape === value}
                    onChange={() => handleShape(value)}
                  />
                  <strong>{label}</strong>
                  <small>{description}</small>
                </label>
              ))}
            </div>
          </fieldset>

          <fieldset className="control-group">
            <legend>Dimensions</legend>
            <div className="field-grid">
              {settings.shape !== "cylinder" ? (
                <label>
                  <span>Width</span>
                  <div className="number-field">
                    <input
                      type="number"
                      min="20"
                      max="500"
                      step="1"
                      value={settings.widthMm}
                      onChange={(event) =>
                        updateSettings({ widthMm: numberValue(event) })
                      }
                    />
                    <em>mm</em>
                  </div>
                </label>
              ) : (
                <label>
                  <span>Inside diameter</span>
                  <div className="number-field">
                    <input
                      type="number"
                      min="20"
                      max="300"
                      step="1"
                      value={settings.diameterMm}
                      onChange={(event) =>
                        updateSettings({ diameterMm: numberValue(event) })
                      }
                    />
                    <em>mm</em>
                  </div>
                </label>
              )}

              <label>
                <span>Height</span>
                <div className="number-field">
                  <input
                    type="number"
                    min="20"
                    max="500"
                    step="1"
                    value={Number(settings.heightMm.toFixed(1))}
                    disabled={lockAspectRatio}
                    onChange={(event) =>
                      updateSettings({ heightMm: numberValue(event) })
                    }
                  />
                  <em>mm</em>
                </div>
              </label>
            </div>

            <label className="toggle-row">
              <input
                type="checkbox"
                checked={lockAspectRatio}
                onChange={(event) => {
                  const checked = event.target.checked;
                  setLockAspectRatio(checked);
                  if (checked && loadedImage) {
                    setSettings((current) => ({
                      ...current,
                      heightMm:
                        (current.shape === "cylinder"
                          ? Math.PI * current.diameterMm
                          : current.widthMm) *
                        (loadedImage.element.naturalHeight /
                          loadedImage.element.naturalWidth),
                    }));
                    setResult(null);
                  }
                }}
              />
              <span>Preserve source-image aspect ratio</span>
            </label>

            {settings.shape === "curved" && (
              <label className="range-field">
                <span>
                  Curve angle <strong>{settings.curveAngleDeg}°</strong>
                </span>
                <input
                  type="range"
                  min="30"
                  max="300"
                  step="5"
                  value={settings.curveAngleDeg}
                  onChange={(event) =>
                    updateSettings({ curveAngleDeg: numberValue(event) })
                  }
                />
              </label>
            )}

            <div className="dimension-note">
              Image-mapped width: {formatDecimal(mappedWidthMm, 1)} mm
            </div>
          </fieldset>

          <fieldset className="control-group">
            <legend>Thickness and detail</legend>
            <div className="field-grid">
              <label>
                <span>Minimum</span>
                <div className="number-field">
                  <input
                    type="number"
                    min="0.4"
                    max="4"
                    step="0.1"
                    value={settings.minThicknessMm}
                    onChange={(event) =>
                      updateSettings({
                        minThicknessMm: numberValue(event),
                      })
                    }
                  />
                  <em>mm</em>
                </div>
              </label>
              <label>
                <span>Maximum</span>
                <div className="number-field">
                  <input
                    type="number"
                    min="0.8"
                    max="8"
                    step="0.1"
                    value={settings.maxThicknessMm}
                    onChange={(event) =>
                      updateSettings({
                        maxThicknessMm: numberValue(event),
                      })
                    }
                  />
                  <em>mm</em>
                </div>
              </label>
              <label>
                <span>Sample pitch</span>
                <div className="number-field">
                  <input
                    type="number"
                    min="0.2"
                    max="2"
                    step="0.05"
                    value={settings.samplePitchMm}
                    onChange={(event) =>
                      updateSettings({
                        samplePitchMm: numberValue(event),
                      })
                    }
                  />
                  <em>mm</em>
                </div>
              </label>
              <label>
                <span>Border</span>
                <div className="number-field">
                  <input
                    type="number"
                    min="0"
                    max="20"
                    step="0.5"
                    value={settings.borderMm}
                    onChange={(event) =>
                      updateSettings({ borderMm: numberValue(event) })
                    }
                  />
                  <em>mm</em>
                </div>
              </label>
            </div>
            <p className="field-help">
              A 0.50 mm sample pitch is a practical default for a 0.4 mm nozzle.
              The app automatically reduces resolution before the mesh becomes
              excessively large.
            </p>
          </fieldset>

          <details className="advanced-panel">
            <summary>Image adjustments</summary>
            <div className="advanced-content">
              <label className="range-field">
                <span>
                  Brightness <strong>{settings.brightness}</strong>
                </span>
                <input
                  type="range"
                  min="-40"
                  max="40"
                  step="1"
                  value={settings.brightness}
                  onChange={(event) =>
                    updateSettings({ brightness: numberValue(event) })
                  }
                />
              </label>
              <label className="range-field">
                <span>
                  Contrast <strong>{settings.contrast}</strong>
                </span>
                <input
                  type="range"
                  min="-40"
                  max="60"
                  step="1"
                  value={settings.contrast}
                  onChange={(event) =>
                    updateSettings({ contrast: numberValue(event) })
                  }
                />
              </label>
              <label className="range-field">
                <span>
                  Gamma <strong>{settings.gamma.toFixed(2)}</strong>
                </span>
                <input
                  type="range"
                  min="0.5"
                  max="2"
                  step="0.05"
                  value={settings.gamma}
                  onChange={(event) =>
                    updateSettings({ gamma: numberValue(event) })
                  }
                />
              </label>
              <label className="toggle-row">
                <input
                  type="checkbox"
                  checked={settings.invert}
                  onChange={(event) =>
                    updateSettings({ invert: event.target.checked })
                  }
                />
                <span>Invert light and dark thickness</span>
              </label>
            </div>
          </details>

          <button
            className="primary-button"
            type="button"
            disabled={!loadedImage || isGenerating}
            onClick={generate}
          >
            {isGenerating ? "Generating…" : "Generate 3D preview"}
          </button>

          {error && <div className="error-message">{error}</div>}
          <p className="status-message">{status}</p>
        </aside>

        <section className="preview-panel">
          <div className="panel-heading preview-heading">
            <div>
              <span>Interactive model</span>
              <h2>Print preview</h2>
            </div>
            <div className="view-toggle" aria-label="Preview appearance">
              <button
                type="button"
                className={previewMode === "backlit" ? "active" : ""}
                onClick={() => setPreviewMode("backlit")}
              >
                Backlit
              </button>
              <button
                type="button"
                className={previewMode === "solid" ? "active" : ""}
                onClick={() => setPreviewMode("solid")}
              >
                Solid
              </button>
            </div>
          </div>

          <ThreePreview geometry={geometryForPreview} mode={previewMode} />

          <div className="preview-footer">
            <span>Drag to rotate</span>
            <span>Scroll to zoom</span>
            <span>Right-drag to pan</span>
          </div>

          {result && (
            <div className="result-area">
              <div className="processed-card">
                <div>
                  <span className="result-label">Processed source</span>
                  <strong>Thickness map</strong>
                </div>
                <img
                  src={result.processedPreviewUrl}
                  alt="Processed grayscale thickness map"
                />
              </div>

              <div className="stats-grid">
                <div>
                  <span>Mesh</span>
                  <strong>
                    {result.stats.columns} × {result.stats.rows}
                  </strong>
                </div>
                <div>
                  <span>Triangles</span>
                  <strong>{formatInteger(result.stats.triangles)}</strong>
                </div>
                <div>
                  <span>Effective pitch</span>
                  <strong>
                    {formatDecimal(result.stats.effectivePitchMm)} mm
                  </strong>
                </div>
                <div>
                  <span>Estimated STL</span>
                  <strong>
                    {formatDecimal(result.stats.estimatedBinaryStlMb, 1)} MB
                  </strong>
                </div>
              </div>

              <div className="export-row">
                <label>
                  <span>STL filename</span>
                  <input
                    type="text"
                    value={outputName}
                    onChange={(event) => setOutputName(event.target.value)}
                  />
                </label>
                <button
                  className="export-button"
                  type="button"
                  onClick={exportStl}
                >
                  Download binary STL
                </button>
              </div>
            </div>
          )}
        </section>
      </section>

      <section className="print-guidance">
        <div>
          <span className="step-number">02</span>
          <div>
            <p className="eyebrow">STARTING PRINT PROFILE</p>
            <h2>Slice it vertically for the best image detail.</h2>
          </div>
        </div>
        <div className="guidance-grid">
          <div>
            <span>Orientation</span>
            <strong>Vertical</strong>
          </div>
          <div>
            <span>Layer height</span>
            <strong>0.12–0.16 mm</strong>
          </div>
          <div>
            <span>Material</span>
            <strong>White PLA</strong>
          </div>
          <div>
            <span>Infill</span>
            <strong>100%</strong>
          </div>
          <div>
            <span>Speed</span>
            <strong>30–40 mm/s</strong>
          </div>
          <div>
            <span>Adhesion</span>
            <strong>6–10 mm brim</strong>
          </div>
        </div>
      </section>

      <footer>
        <span>RSB23 Lithophane Studio</span>
        <span>Local browser processing · Binary STL output</span>
      </footer>
    </main>
  );
}
